import { useEffect, useRef, useState } from "react";
import type { TextInputInstance } from "react-native";
import { Platform, TextInput, type TextInputProps } from "react-native";

import { cn } from "@/lib/ui/cn";
import { useThemeColors } from "@/theme/theme";

/**
 * The iOS keypads with no key that closes the keyboard unless the field asks for one (TRO-1610).
 * Android's numeric keypads carry their own action key, so they are left alone.
 */
const IOS_KEYPADS_WITHOUT_RETURN = new Set<TextInputProps["keyboardType"]>([
  "number-pad",
  "decimal-pad",
  "numeric",
]);

/**
 * The shared text field.
 *
 * Besides the resting border it carries a focus ring (primary border and soft fill) while active,
 * and an `error` state (loss border) so a validation failure shows on the field itself. The
 * consumer `className` still wins, so a borderless field inside its own framed row stays flat.
 *
 * A numeric field on iOS gets a Done key by default, so the keyboard can always be closed; a
 * caller's own `returnKeyType` wins.
 */
export function Input(
  { className, error, onFocus, onBlur, type, returnKeyType, ...rest }:
    & TextInputProps
    & {
      className?: string;
      /** Marks the field invalid with the loss border. */
      error?: boolean;
      /** The HTML input type on web, such as `"email"`; ignored on native. */
      type?: string;
    },
) {
  const c = useThemeColors();
  const inputRef = useRef<TextInputInstance & { type?: string; }>(null);
  const [focused, setFocused] = useState(false);
  useEffect(() => {
    if (Platform.OS !== "web" || !type) return;
    if (inputRef.current) inputRef.current.type = type;
  }, [type]);

  return (
    <TextInput
      ref={inputRef}
      placeholderTextColor={c.subtle}
      returnKeyType={returnKeyType
        ?? (Platform.OS === "ios" && IOS_KEYPADS_WITHOUT_RETURN.has(rest.keyboardType)
          ? "done"
          : undefined)}
      onFocus={(e) => {
        setFocused(true);
        onFocus?.(e);
      }}
      onBlur={(e) => {
        setFocused(false);
        onBlur?.(e);
      }}
      className={cn(
        "h-11 rounded-lg border px-3 text-[15px] font-body text-foreground",
        error
          ? "border-loss bg-surface"
          : focused
          ? "border-primary bg-accent-soft"
          : "border-border bg-surface",
        className,
      )}
      {...rest}
    />
  );
}
